import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ClassRoom } from '../classroom.js';
import { Exam } from '../exam.js';
import { normalizeSubject } from '../subjects.js';
import { buildClassName, findTeacherClassroomByNameAndSubject, parseClassName } from '../utils/classroom.js';
import { Teacher } from '../teacher.js';

/**
 * Renames one of the current teacher's classes and/or moves it to another subject.
 *
 * The raw name is put through `parseClassName`/`buildClassName` — the same normalization every
 * automatically created classroom goes through — so a hand-typed "12 lk" is stored exactly as the
 * agent would have stored it, and `grade`/`suffix` stay consistent with `name` instead of going stale
 * the moment a teacher edits it by hand.
 *
 * Linked exams follow the rename: `Exam.class` is the display copy of this classroom's name (the real
 * link is `Exam.classRoomID`), so leaving it behind would have the exam claim a class label that no
 * longer exists anywhere. `Exam.subject` is deliberately NOT touched — that is the exam's own fact,
 * drives master-solution and grading-scheme generation, and is not the classroom's to overwrite.
 */
export const updateClassroomMutation = registerMutation({
  operationName: 'updateClassroomMutation',
  type: () => ClassRoom,
  params: [
    { name: 'classRoomID', type: () => String },
    { name: 'name', type: () => String },
    { name: 'subject', type: () => String },
  ],

  resolve:
    (classRoomID: string, name: string, subject: string) => async (_gqlFields: BaseQLSelectionSet<ClassRoom>) => {
      const trimmedClassRoomID = typeof classRoomID === 'string' ? classRoomID.trim() : '';
      const rawName = typeof name === 'string' ? name.trim() : '';
      if (!trimmedClassRoomID) throw new Error('Klasse nicht gefunden');
      if (!rawName) throw new Error('Klassenname darf nicht leer sein');

      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
      if (!currentUser?._id) throw new Error('Nicht autorisiert: Kein User eingeloggt');

      const teacherCollection = await getCollection(Teacher);
      const teacher = await teacherCollection.findOne({ userID: currentUser._id });
      if (!teacher) throw new Error('Nicht autorisiert: Kein Lehrerprofil gefunden');

      const classroomCollection = await getCollection(ClassRoom);
      const classroom = await classroomCollection.findOne({ _id: trimmedClassRoomID });
      if (!classroom) throw new Error('Klasse nicht gefunden');
      if (classroom.teacherID !== teacher._id) throw new Error('Nicht autorisiert: Diese Klasse gehört dir nicht');

      const { grade, suffix } = parseClassName(rawName);
      const resolvedName = buildClassName(grade, suffix) || rawName;
      const resolvedSubject = normalizeSubject(subject);

      // A teacher's classes are keyed by (name, subject) everywhere that resolves one from text — see
      // `linkExamToClassroom`. Renaming onto an existing pair would leave two classrooms that every such
      // lookup has to choose between arbitrarily, so it is refused rather than silently merged.
      const clash = await findTeacherClassroomByNameAndSubject(teacher._id, resolvedName, resolvedSubject);
      if (clash && clash._id !== trimmedClassRoomID) throw new Error('Es gibt bereits eine Klasse mit diesem Namen');

      await classroomCollection.updateOne(
        { _id: trimmedClassRoomID },
        { $set: { name: resolvedName, grade: grade || null, suffix: suffix ?? null, subject: resolvedSubject } },
      );

      // `updateMany` is not available on the wrapped collection (see `wrapCollection` in baseql), and the
      // raw one skips the publish filter — scoped by `classRoomID`, already proven to be this teacher's.
      if (resolvedName !== classroom.name) {
        const rawExams = await getRawCollection(Exam._collectionName);
        await rawExams.updateMany({ classRoomID: trimmedClassRoomID }, { $set: { class: resolvedName } });
      }

      logger.info(
        { classRoomID: trimmedClassRoomID, teacherID: teacher._id, name: resolvedName, subject: resolvedSubject },
        'Classroom updated',
      );

      return new ClassRoom({
        ...classroom,
        name: resolvedName,
        grade: grade || null,
        suffix: suffix ?? null,
        subject: resolvedSubject,
      });
    },
});
